import Dexie, { PromiseExtended } from 'dexie';
import { from, Observable } from 'rxjs';
import { cryptoOptions } from 'dexie-encrypted';
import { applyMiddlewareWithCustomEncryption } from 'dexie-encrypted/dist/applyMiddleware';
import { encryptWithNacl, decryptWithNacl } from './encryption';
 

// #####
// attention !!! aucune référence aux vues ici, ni viewdata. Utilisé pour le worker
// #####

// FormulaEngineInstance tel que stocké en cache local
export interface LocalFei {
  key: string; // clé d'unicité du fei level#scen
  level: number;
  scen: string;
  revisionNumber: number; // Numéro de dernière update enregistré en local
  localBaselines?: {[key: string]: any}; // baselines
}

/** stockage local des infos de FEI */
export interface BddFEI {
  key: string;
  level: number;
  scen: string;
  value: LocalFei;
}

// objet local de stockage des visions (lite)
export interface LocalVision {
  /** Numéro minimal autorisé avant rechargement éventuel de la BU ou de l'ensemble de FD */
  minRevNumber: number;
  /** code unique de la vision */
  key: string; // domain|bu|vision (* sur vision pour BU)
  domain: string;
  businessUnitCode: string;
  vision: string;
  isBU: boolean; // c'est une BU ? (vision * de configuration)
  // nbStep: number; // nombre de step de la vision pour suppression en masse
}

// objet local de stockage des visions
export interface BddVision {
  /** code unique de la vision */
  key: string; // domain|bu|vision (* sur vision pour BU)
  value: LocalVision;
}

/** stockage des baselines, par clé de FEI */
export interface BddBaseline {
  id: number;
  feiKey: string;
  key: string;
  level: number;
  scen: string;
  value: any;
}

/** stockage des médias */
export interface BddMedia {
  key: string;
  value: any;
  thumb: any;
}

// objet local de stockage des FDs
export interface LocalFreedata {
  key: string; // domain|bu
  /** Numéro de dernière update enregistré en local */
  revisionNumber: number;
  /** Les freedatas locaux */
  freedatas: any[];
}


/** stockage des Freedatas */
export interface BddFreedata {
  id: number;
  buKey: string;
  code: string;
  revisionNumber: number;
  value: any; // LocalFreedata;
}

/** stockage des baselines, par clé de FEI */
export interface BddFreedataValue {
  id: number;
  buKey: string;
  fdKey: string;
  value: any;
}

// objet local de stockage des Workcoms
export interface LocalWorkcom {
  /** code unique du  Workcom */
  key: string; //naturalID
  buKey: string;// domain|bu|vision (* sur vision pour BU)
  value: any; 
}


/** stockage des messages (Workcom) */
export interface BddWorkcom {
  id: number;
  key: string;
  buKey: string;
  value: any; // Workcom;
}

// objet local de stockage des matrices
export interface LocalMatrix {
  key: string; // code de la matrice
  buKey: string; // domain|bu
  version: number; // version en BDD
  value: any; // la matrice complète (SANS le ROWS);
}

/**  stockage des matrices */
export interface BddMatrix {
  id: number;
  key: string;
  buKey: string;
  value: any; // Matrix (SANS le ROWS);
}

// objet local de stockage des matrices
export interface LocalMatrixData {
  key: string; // code de la matrice
  buKey: string; // domain|bu
  version: number; // version en BDD
  value: any; // la matrice complète (SANS le ROWS);
}

/**  stockage des matrices: ROWS */
export interface BddMatrixData {
  id: number;
  matrixid: number; // id de la matrice
  key: string; // code de la matrice
  buKey: string; // domain|bu
  // ventilation par colonnes;
  0?: any;
  1?: any;
  2?: any;
  3?: any;
  4?: any;
  5?: any;
  6?: any;
  7?: any;
  8?: any;
  9?: any;
  10?: any;
  11?: any;
  12?: any;
  13?: any;
  14?: any;
  15?: any;
  16?: any;
  17?: any;
  18?: any;
  19?: any;
  20?: any;
  21?: any;
  22?: any;
  23?: any;
  24?: any;
  25?: any;
  26?: any;
  27?: any;
  28?: any;
  29?: any;
}


/**
 * Service d'accès à la base locale IndexedDB
 *
 * @author LMA
 */

export class AkmBDD extends Dexie {
 
  // Pagination : taille des paquets pour écriture en BDD locale
  public static BDD_CHUNK_SIZE: number = 50000;

  private static _INSTANCE: AkmBDD;

  public static getInstance(): AkmBDD {
    if (!AkmBDD._INSTANCE) {
      AkmBDD._INSTANCE = new AkmBDD();
    }
    return AkmBDD._INSTANCE;
  }

  public static bddName: string = 'allkeyme';
  public static version: number = 1;

  // les noms de stores
  public static BDD_MEDIA_KEY: string = 'media';
  public static BDD_FREEDATA_KEY: string = 'freedata';
  // public static BDD_FREEDATAVALUE_KEY: string = 'freedatavalue';
  public static BDD_BASELINE_KEY: string = 'baseline';
  public static BDD_FEI_KEY: string = 'fei';
  public static BDD_VISION_KEY: string = 'vision';
  public static BDD_WORKCOM_KEY: string = 'workcom';
  public static BDD_MATRIX_KEY: string = 'matrix';
  public static BDD_MATRIX_DATA_KEY: string = 'matrixdata';


  /** construction d'une BDD pour le worker / ou principal, premier arrivé */
  public static storeMeImFamous(dex: Dexie): void {
    dex.version(AkmBDD.version).stores({
      fei: 'key, level, scen',
      vision: 'key',
      baseline: 'id, feiKey, level, scen',
      media: 'key',
      freedata: 'id, buKey',
      workcom: 'id, key, buKey, [key+buKey]',
      matrix: 'id, key, buKey, [key+buKey]',
      matrixdata: '++id, matrixid, key, buKey, [key+buKey]',
      // freedatavalue: 'id, buKey, fdKey'
    });
  }

  public static async onCryptKeyChange(param: any): Promise<any> {
    return null;
  }

  constructor() {  
    super(AkmBDD.bddName + AkmBDD.version);
    
    // Dexie.delete('allkeyme1'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme2'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme3'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme4'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme5'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme6'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme7'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme8'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme9'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme10'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme11'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme12'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme13'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme14'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme15'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme16'); // forcer la suppression de l'ancienne BDD
    Dexie.delete('allkeyme17'); // forcer la suppression de l'ancienne BDD

    let key: string = 'c429601ee7a6167356f15baa70fd8fe1';
    let key8: Uint8Array = Uint8Array.from(key.split('').map(letter => letter.charCodeAt(0)));
    

    applyMiddlewareWithCustomEncryption({
        db: this,
        encryptionKey : key8,
        tableSettings: {
          baseline: cryptoOptions.NON_INDEXED_FIELDS, 
          freedata: cryptoOptions.NON_INDEXED_FIELDS,
          workcom: cryptoOptions.NON_INDEXED_FIELDS,
        },//matrixdata: cryptoOptions.NON_INDEXED_FIELDS},
        encrypt: encryptWithNacl, // <--- right here
        decrypt: decryptWithNacl, // <--- and here
        onKeyChange: AkmBDD.onCryptKeyChange,
    });
        

    AkmBDD.storeMeImFamous(this);

    // this.open() //opening the database
    // .then(data => console.log("DB Opened"))
    // .catch(err => console.log("wiz", err.message));
  }

  /** suppression d'un store */
  public clear(store: string): void {
    AkmBDD.getInstance().table(store).clear();
  }

  /** retourne toute la table */
  public getAll(store: string): Observable<any> {
    return from(AkmBDD.getInstance().table(store).toArray());
  }

  /** lecture unitaire */
  public doGet(store: string, key: any): Observable<any> {
    // console.log('bdd get', store, key);
    return from(AkmBDD.getInstance().table(store).get(key));
  }

  /** chargement du FEI et de ses Baseline par widgetDataService */
  public doGetFei(key: string, proto: LocalFei): PromiseExtended<LocalFei> {
    // console.log('dogetFEi', key);
    return AkmBDD.getInstance().table(AkmBDD.BDD_FEI_KEY).get(key).then((local: BddFEI) => {
        if(!!local) {
          proto.revisionNumber = local.value.revisionNumber;
        }
        return local; // le FEI
    }).then ((local: BddFEI) => {
        if(!!local) {
          return AkmBDD.getInstance().table(AkmBDD.BDD_BASELINE_KEY).where("feiKey").equals(local.key).toArray(); // ses baselines
        }
        return null;
    }).then ((bls: BddBaseline[]) => {
      bls?.forEach((bl: BddBaseline) => {
        if(!proto.localBaselines) {
          proto.localBaselines =  {};
        }
        proto.localBaselines[bl.value.key] = bl.value;
        // proto.localBaselines[bl.value.key] = bl.value;
        // bl.value.trios = null; // suppression des trios si existent... sinon pas chargés
      });
      // console.log('proto', proto);
      return proto;
    });
  }


  /** ajout unitaire en BDD */
  public doAdd(store: string, key: any, value: any): void {
    // console.log('bdd add', store, key, value);
    AkmBDD.getInstance().table(store).put(value, key);
  }

  /** ajout unitaire en BDD */
  public doDelete(store: string, key: any): void {
    AkmBDD.getInstance().table(store).delete(key);
  }

  /**
   * collecte des FDs de la bu
   * @param clé domain|bu
   */
  public doFindBuFreedatas(buKey: string): Observable<BddFreedata[]> {
    return from(AkmBDD.getInstance().table(AkmBDD.BDD_FREEDATA_KEY).where("buKey").equals(buKey).toArray());
  }

  /** persistence en masse des freedatas */
  public doBulkFreedatas(pairs: BddFreedata[]): void {
    // console.log('bdd bulk BddFreedata', pairs);
    AkmBDD.getInstance().table(AkmBDD.BDD_FREEDATA_KEY).bulkPut(pairs).then(num => {
        
        // console.log('doBulkFreedatas ok');
      }).catch(reason => {
        console.log('oups bulk freedata', reason);
      });
  }

  /** suppression en masse */
  public doBulkDelete(store: string, keys: any[], dataload?: any, callback?: Function): void {
    AkmBDD.getInstance().table(store).bulkDelete(keys).then(num => {
      // console.log('doBulkDelete and then...', num, dataload);
      if(!!callback) {
        callback(dataload);
      }
    }).catch(reason => {
      console.log('oups bulk', reason);
    })
    ;
  }

  /** suppression en masse par ID source (matrices) */
  /**
   * 
   * @param store BDD MatrixData
   * @param ids matrixIds à supprimer
   * @param dataload 
   * @param callback 
   */
  public doBulkDeleteMatrixData(store: string, ids: any[], dataload?: any, callback?: Function): void {
    
    // console.log('doBulkDeleteMatrixData', store, ids);
    AkmBDD.getInstance().table(store).where('matrixid').anyOf(ids).delete().then(num => {
      // console.log('doBulkDelete and then...', num, dataload);
      if(!!callback) {
        callback(dataload);
      }
    }).catch(reason => {
      console.log('delete by id', reason);
    })
    ;
  }


  /** baselines du FEI */
  public doFindFeiBaselines(feiKey: string): Observable<any> {
    return from(AkmBDD.getInstance().table(AkmBDD.BDD_BASELINE_KEY).where("feiKey").equals(feiKey).toArray());
  }


  /** 
   * persistence en masse des baselines
   * 
   * @param pairs 
   * @param dataload 
   * @param callback véritable pour retour au widgetDataService
   * @param workerCallback fonction de retour dans le work
   */
  public doBulkAddBaselines(pairs: BddBaseline[], dataload?: any, callback?: Function, workerCallback?: Function): void {
    // console.log('bdd bulk baselines', pairs);
    let slices: BddBaseline[][] = [];
    while(pairs.length > 0) {
      let slice: BddBaseline[] = pairs.splice(0, AkmBDD.BDD_CHUNK_SIZE);
      slices.push(slice);
    }
    
    AkmBDD.asyncBulk(slices, dataload, callback, workerCallback); 

  }

  private static asyncBulk(slices: BddBaseline[][], dataload?: any, callback?: Function, workerCallback?: Function): void {
    let slice: BddBaseline[] = slices.pop();
    if(slice) {
      AkmBDD.getInstance().table(AkmBDD.BDD_BASELINE_KEY).bulkPut(slice).then(num => {
        // console.log('doBulkslice, and then...', num);
        // console.log('doBulkslice, and then...', num, slice);
        AkmBDD.asyncBulk(slices,dataload, callback, workerCallback);
      }).catch(reason => {
        console.log('oups bulk', reason);
      })
    } else {
      if(!!workerCallback) {
        workerCallback(dataload, callback);
      } else if(!!callback) { // appel par service d'init des dataloads
        callback(dataload);
      }
    }
  }

  /**
   * collecte des Workcoms de la vision
   * @param clé bu / vision
   */
  public doFindWorkcoms(buKey: string): Observable<BddWorkcom[]> {
    return from(AkmBDD.getInstance().table(AkmBDD.BDD_WORKCOM_KEY).
    where('buKey').equals(buKey).toArray());
  }

  /** persistence en masse des workcom */
  public doBulkWorkcoms(pairs: BddWorkcom[]): void {
   //  console.log('bdd bulk BddWorkcom', pairs);
    AkmBDD.getInstance().table(AkmBDD.BDD_WORKCOM_KEY).bulkPut(pairs).then(num => {
        
        // console.log('doBulkFreedatas ok');
      }).catch(reason => {
        console.log('oups bulk Workcom', reason);
      });
  }

  /**
   * collecte des matrices de la BU
   * les ROWS ne sont pas dispo, il faudra faire un get sur MatrixData
   * @param clé dom/BU
   */
  public doFindMatrix(buKey: string): Observable<BddMatrix[]> {
    return from(AkmBDD.getInstance().table(AkmBDD.BDD_MATRIX_KEY).
    where('buKey').equals(buKey).toArray());
  }

  /** persistence en masse des matrices (sans matrROWS) */
  public doBulkMatrix(pairs: BddMatrix[]): void {
   //  console.log('bdd bulk doBulkMatrix', pairs);
    AkmBDD.getInstance().table(AkmBDD.BDD_MATRIX_KEY).bulkPut(pairs).then(num => {
        
        // console.log('doBulkMatrix ok');
      }).catch(reason => {
        console.log('oups bulk matrix', reason);
      });
  }

  /** persistence en masse des matrices (les ROWS) */
  public doBulkMatrixData(pairs: BddMatrixData[]): void {
   //  console.log('bdd bulk doBulkMatrix', pairs);
    AkmBDD.getInstance().table(AkmBDD.BDD_MATRIX_DATA_KEY).bulkPut(pairs).then(num => {
        
        // console.log('doBulkMatrixData ok');
      }).catch(reason => {
        console.log('oups bulk matrixData', reason);
      });
  }

  /**
   * lecture de matrice semi-synchrone (appel de puis ref.func vers worker & while)
   * @param matrixKey la clé de matrice
   * @param query la requête
   * @returns 
   */
  public runMatrixQuery(matrixKey: string, query: any): Promise<BddMatrixData[]> {
    return AkmBDD.getInstance()
      .table(AkmBDD.BDD_MATRIX_DATA_KEY)
      .where('key')
      .equals(matrixKey)
      .filter(query)
      .toArray()
      .catch(err => {
        console.error('runMatrixQuery error', err);
        return [];
      });
  }

}






